import { BaseSyntheticEvent, useCallback, useMemo } from 'react';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { AllowanceProposalProps } from '~/data/proposals';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  CommonProposalFieldProps,
  EmployeeProposalDecision,
  EmployeeProposalUpdateCallback,
  UseCommonProposalFormOutput,
  commonEnumOptionFiltering,
  transformProposalRequestProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useCommonFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useCommonFieldProps';
import { useInitiateFormData } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useInitiateFormData';
import { useProposalPermissions } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalPermissions';

import { useEmploymentEnumsFilter } from '../Common/useEmploymentEnumsFilter';
import { useScheduleEmployments } from '../Common/useScheduleEmployments';

import {
  AllowanceProposalFormData,
  mapDefaultData,
  mapFormDataToLegacyPayload,
  submitAllowanceProposal,
  useValidationSchema,
} from './data';

type PropsType = {
  proposal: AllowanceProposalProps;
  employeeId: number;
  onProposalUpdate: EmployeeProposalUpdateCallback;
};

type UseAllowanceProposalFormOutput =
  UseCommonProposalFormOutput<AllowanceProposalFormData> & {
    enums: {
      allowanceTypes: Enum[];
      eventFrequencies: Enum[];
    };
    fieldProps: CommonProposalFieldProps;
  };

export const useAllowanceProposalForm = (
  props: PropsType,
): UseAllowanceProposalFormOutput => {
  const { proposal, employeeId, onProposalUpdate } = props;
  const { proposalData } = proposal;

  const {
    readOnlyMode,
    userRoles,
    loggedInEmployeeId,
    canSubmitForm,
    responsibleHRSelectEnabled,
  } = useProposalPermissions({ proposal });
  const { hasUpcomingEmployments } = useScheduleEmployments();

  const { filterActiveEnums } = useEmploymentEnumsFilter();

  const resolver = useYupValidationResolver(
    useValidationSchema(hasUpcomingEmployments),
    'react-hook-form',
  );

  const {
    allResponsibleHRIds,
    isLoading,
    form,
    handleFormReset,
    handleSubmitRequest,
  } = useInitiateFormData<AllowanceProposalProps, AllowanceProposalFormData>({
    proposal,
    employeeId,
    dataMapper: mapDefaultData,
    resolver,
  });

  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    const { filterEnumsByCodes, rejectCodes } = commonEnumOptionFiltering;

    const getEnumOptions = (
      listKey: EnumListKey,
      selectedValue?: string,
    ): Enum[] => filterActiveEnums(getEnums(listKey), selectedValue);

    return {
      allowanceTypes: getEnumOptions(
        EnumListKey.ALLOWANCE_TYPE,
        proposalData?.allowanceTypeFK,
      ),
      eventFrequencies: filterEnumsByCodes(
        getEnumOptions(EnumListKey.EVENT_FREQUENCY, proposalData?.frequencyFK),
        rejectCodes.EVENT_FREQUENCY,
      ),
    };
  }, [
    proposalData?.allowanceTypeFK,
    proposalData?.frequencyFK,
    filterActiveEnums,
    getEnums,
  ]);

  const handleFormSubmit = useCallback(
    (
      decision?: EmployeeProposalDecision,
    ): ((e?: BaseSyntheticEvent | undefined) => Promise<void>) => {
      return handleSubmitRequest({
        onSuccess: onProposalUpdate,
        handleSubmission: (data, requestProps) => {
          requestProps = transformProposalRequestProps(
            requestProps,
            +data.employmentId,
          );
          return submitAllowanceProposal(
            mapFormDataToLegacyPayload({
              data,
              isHRUser: userRoles.isHR,
              loggedInEmployeeId,
              decision,
              requestProps,
            }),
            requestProps,
          );
        },
      });
    },
    [handleSubmitRequest, onProposalUpdate, userRoles, loggedInEmployeeId],
  );

  const { watch } = form;
  const { isLoading: isLoadingCommonFieldProps, ...fieldProps } =
    useCommonFieldProps({
      startDate: { value: watch('startDate') },
      endDate: { value: watch('endDate') },
      employmentId: watch('employmentId'),
    });

  return {
    form,
    enums,
    responsibleHRSelector: {
      options: allResponsibleHRIds,
      isResponsibleHR: userRoles.isResponsibleHR,
      enabled: responsibleHRSelectEnabled,
    },
    isLoading: {
      responsibleHR: isLoading,
      commonFieldProps: isLoadingCommonFieldProps,
    },
    handleFormSubmit,
    handleFormReset,
    readOnlyMode,
    canSubmitForm,
    fieldProps,
  };
};
